import React, { useEffect, useRef, useState } from "react";
import { Mic, Volume2, RotateCcw } from "lucide-react";
import { openMic, detectPitch, freqToMidi, midiName } from "@/lib/mic";
import { playPitch } from "@/lib/audio";
import { pitchToFreq } from "@/lib/music";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VoicePartSongs } from "@/components/VoicePartSongs";

const UP = ["A3", "B3", "C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5", "G5", "A5", "B5", "C6"];
const DOWN = ["A3", "G3", "F3", "E3", "D3", "C3", "B2", "A2", "G2", "F2", "E2", "D2", "C2"];
const midiOf = (p) => freqToMidi(pitchToFreq(p));
const close = (heard, target) => [-12, 0, 12].some((o) => Math.abs(heard - target + o) <= 1);
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Male voices reach below D#3; the chest-to-head split then separates Bass/Tenor and Alto/Soprano.
export const partFor = (low, split) => {
  if (low <= 51) return low <= 45 || split <= 62 ? "Bass" : "Tenor";
  return low <= 55 || split <= 64 ? "Alto" : "Soprano";
};

const Btn = ({ onClick, testId, primary, children, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testId}
    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-transform hover:scale-105 disabled:opacity-50 ${primary ? "bg-primary text-primary-foreground" : "border border-border text-foreground hover:border-primary"}`}>
    {children}
  </button>
);

const Ladder = ({ list, idx, marks, testId }) => (
  <div className="mt-3 flex flex-wrap gap-1.5" data-testid={testId}>
    {list.map((p, k) => (
      <span key={p} className={`rounded-md px-2 py-1 font-mono text-xs ${k === idx ? "bg-primary text-primary-foreground" : marks[p] === "ok" ? "bg-emerald-500/20 text-emerald-300" : marks[p] === "head" ? "bg-sky-500/20 text-sky-300" : marks[p] === "miss" ? "bg-red-500/20 text-red-300" : "bg-muted text-muted-foreground"}`}>
        {p}
      </span>
    ))}
  </div>
);

export const ScaleRangeFinder = ({ testId = "scale-range" }) => {
  const { user } = useAuth();
  const [phase, setPhase] = useState("intro");
  const [idx, setIdx] = useState(0);
  const [status, setStatus] = useState("");
  const [heard, setHeard] = useState(null);
  const [marks, setMarks] = useState({});
  const [res, setRes] = useState({ top: null, split: null, low: null });
  const [err, setErr] = useState("");
  const mic = useRef(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; mic.current?.stop(); }; }, []);

  const list = phase === "down" ? DOWN : UP;
  const note = list[idx];

  const tryNote = async () => {
    setErr(""); setHeard(null);
    if (!mic.current) {
      try { mic.current = await openMic(); } catch { setErr("Microphone access was blocked. Allow the microphone in your browser and try again."); return; }
    }
    setStatus("play"); playPitch(note, 1.0); await wait(1300);
    if (!alive.current) return;
    setStatus("listen");
    const target = midiOf(note), end = performance.now() + 2600;
    let hits = 0;
    while (performance.now() < end && alive.current) {
      const f = detectPitch(mic.current.read(), mic.current.ctx.sampleRate);
      if (f && f > 60 && f < 1300) { const m = freqToMidi(f); setHeard(m); if (close(m, target)) hits += 1; }
      await wait(30);
    }
    const ok = hits >= 10;
    setMarks((x) => ({ ...x, [note]: ok ? "ok" : "miss" }));
    setStatus(ok ? "heard" : "missed");
  };

  const next = () => { setStatus(""); setHeard(null); if (idx + 1 < list.length) setIdx(idx + 1); else (phase === "up" ? flip("end") : finishDown()); };
  // mode: "head" = voice flipped on this note, "fail" = couldn't reach it, "end" = sang the whole ladder
  const flip = (mode) => {
    if (mode === "head") setMarks((x) => ({ ...x, [note]: "head" }));
    const top = mode === "end" ? note : UP[Math.max(0, idx - 1)];
    setRes((r) => ({ ...r, top, split: mode === "head" ? note : null }));
    setPhase("down"); setIdx(1); setStatus(""); setHeard(null);
  };
  const finishDown = (stopHere = false) => {
    const reached = DOWN.slice(0, stopHere ? idx : idx + 1).filter((p) => marks[p] === "ok" || p === "A3");
    const low = reached[reached.length - 1] || "A3";
    const part = partFor(midiOf(low), midiOf(res.split || res.top));
    if (user) api.put("/me/voice-range", { low, high: res.top, part }).catch(() => {});
    setRes((r) => ({ ...r, low, part }));
    setPhase("done"); setStatus(""); mic.current?.stop(); mic.current = null;
  };
  const restart = () => { setPhase("intro"); setIdx(0); setMarks({}); setRes({ top: null, split: null, low: null }); setStatus(""); setErr(""); };

  return (
    <div className="mt-5 rounded-2xl border border-primary/30 bg-card p-4 sm:p-6" data-testid={testId}>
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Mic size={14} /> Find your range: copy the scale</p>
      {phase === "intro" && <Intro testId={testId} onStart={() => setPhase("up")} />}
      {(phase === "up" || phase === "down") && (
        <Step {...{ testId, phase, note, list, idx, marks, status, heard, tryNote, next, flip, finishDown }} />
      )}
      {phase === "done" && <Result testId={testId} res={res} onRestart={restart} saved={!!user} />}
      {err && <p className="mt-3 text-sm text-red-400" data-testid={`${testId}-error`}>{err}</p>}
    </div>
  );
};

const Intro = ({ testId, onStart }) => (
  <div className="mt-3 space-y-3 text-sm text-muted-foreground" data-testid={`${testId}-intro`}>
    <p>You'll hear one note at a time, starting on the <b className="text-foreground">A below Middle C</b>. Copy each note on "ah". First we go <b className="text-foreground">up</b> the scale, listening for where your natural voice changes into your head voice. Then we go <b className="text-foreground">down</b> from the same A.</p>
    <p className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-amber-200" data-testid={`${testId}-sing-out`}>Sing out, so the microphone can hear you clearly. Use headphones if you can, and don't strain.</p>
    <Btn primary onClick={onStart} testId={`${testId}-start`}><Mic size={13} /> Start (allow the microphone)</Btn>
  </div>
);

const Step = ({ testId, phase, note, list, idx, marks, status, heard, tryNote, next, flip, finishDown }) => {
  const up = phase === "up";
  return (
    <div className="mt-3" data-testid={`${testId}-step`}>
      <p className="text-sm font-semibold" data-testid={`${testId}-phase`}>{up ? "Going up from A below Middle C" : "Going down from A below Middle C"}</p>
      <Ladder list={list} idx={idx} marks={marks} testId={`${testId}-ladder`} />
      <p className="mt-4 font-serif text-4xl text-primary" data-testid={`${testId}-note`}>{note}</p>
      <p className="mt-1 text-sm text-muted-foreground" data-testid={`${testId}-status`}>
        {status === "play" ? "Listen…" : status === "listen" ? "Now sing it — sing out!" : status === "heard" ? "Got it! How did that note feel?" : status === "missed" ? "I couldn't hear that note clearly. Sing out and try again." : "Press Play & sing when you're ready."}
        {heard != null && (status === "listen" || status === "missed") && <span className="ml-2" data-testid={`${testId}-heard`}>I hear: <b className="text-foreground">{midiName(heard)}</b></span>}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {(status === "" || status === "missed") && <Btn primary onClick={tryNote} testId={`${testId}-play`}><Volume2 size={13} /> {status === "missed" ? "Try again" : "Play & sing"}</Btn>}
        {status === "heard" && <Btn primary onClick={next} testId={`${testId}-natural`}>{up ? "Still my natural voice — next note" : "Comfortable — next note down"}</Btn>}
        {status === "heard" && up && <Btn onClick={() => flip("head")} testId={`${testId}-flipped`}>My voice changed to head voice</Btn>}
        {(status === "missed" || status === "heard") && !up && <Btn onClick={() => finishDown(status === "missed")} testId={`${testId}-too-low`}>That's as low as I can go</Btn>}
        {status === "missed" && up && <Btn onClick={() => flip("fail")} testId={`${testId}-too-high`}>That's too high for me</Btn>}
      </div>
    </div>
  );
};

const Result = ({ testId, res, onRestart, saved }) => (
  <div className="mt-3 space-y-2 text-sm" data-testid={`${testId}-result`}>
    <p>Your natural voice: <b data-testid={`${testId}-range`}>{res.low}–{res.top}</b></p>
    {res.split && <p>Your voice changed to head voice at: <b data-testid={`${testId}-split`}>{res.split}</b></p>}
    <p className="text-base">Your voice part: <b className="text-primary" data-testid={`${testId}-part`}>{res.part}</b></p>
    <p className="text-xs text-muted-foreground">Where your natural voice changes into your head voice is a good indication of your range. It's a guide — every voice is different, and your range grows with practice.{saved ? " Saved to your account." : ""}</p>
    <Btn onClick={onRestart} testId={`${testId}-restart`}><RotateCcw size={12} /> Try again</Btn>
    <VoicePartSongs part={res.part} testId={`${testId}-songs`} />
  </div>
);
